import { useCallback, useEffect, useState } from 'react';
import { getCashMovements } from '../services';
import { CashBox, ICashMovement, ICashMovementsData, CashMovementCategory } from '../types';
import { resolveErrorMessage } from './hookUtils';

interface CashMovementsFilters {
	box?: CashBox;
	currency?: string;
	category?: CashMovementCategory;
	from?: string;
	to?: string;
}

interface CashMovementsState {
	movements: ICashMovement[];
	pagination: ICashMovementsData['pagination'] | null;
	loading: boolean;
	loadingMore: boolean;
	error: string | null;
	cachedAt: string | null;
	loadMore: () => void;
	hasMore: boolean;
	refetch: () => void;
}

export function useCashMovements(
	branch: string | undefined,
	filters: CashMovementsFilters = {},
	pageSize = 30,
): CashMovementsState {
	const [movements, setMovements] = useState<ICashMovement[]>([]);
	const [pagination, setPagination] = useState<ICashMovementsData['pagination'] | null>(null);
	const [loading, setLoading] = useState(true);
	const [loadingMore, setLoadingMore] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [cachedAt, setCachedAt] = useState<string | null>(null);
	const [offset, setOffset] = useState(0);
	const [tick, setTick] = useState(0);

	const { box, currency, category, from, to } = filters;

	useEffect(() => {
		if (!branch) {
			setLoading(false);
			return;
		}

		let cancelled = false;
		const isFirstPage = offset === 0;
		if (isFirstPage) {
			setLoading(true);
			setError(null);
		} else {
			setLoadingMore(true);
		}

		getCashMovements({ branch, box, currency, category, from, to, limit: pageSize, offset })
			.then(({ data, cachedAt: newCachedAt }) => {
				if (cancelled) return;
				setMovements((prev) => (isFirstPage ? data.movements : [...prev, ...data.movements]));
				setPagination(data.pagination);
				setCachedAt(newCachedAt);
				setLoading(false);
				setLoadingMore(false);
			})
			.catch((err) => {
				if (cancelled) return;
				setError(resolveErrorMessage(err));
				setLoading(false);
				setLoadingMore(false);
			});

		return () => {
			cancelled = true;
		};
	}, [branch, box, currency, category, from, to, pageSize, offset, tick]);

	useEffect(() => {
		setMovements([]);
		setPagination(null);
		setOffset(0);
	}, [branch, box, currency, category, from, to]);

	const hasMore = pagination !== null && movements.length < pagination.total;

	const loadMore = useCallback(() => {
		if (loading || loadingMore) return;
		setOffset((prev) => prev + pageSize);
	}, [loading, loadingMore, pageSize]);

	const refetch = useCallback(() => {
		setOffset(0);
		setTick((t) => t + 1);
	}, []);

	return { movements, pagination, loading, loadingMore, error, cachedAt, loadMore, hasMore, refetch };
}
